/**
 * Sports AI Oracle
 * Motor central de respuestas deportivas. Usa heurísticas determinísticas
 * primero (token-cheap) y delega narrativa al LLM solo cuando aporta valor.
 */
import type { Player } from "@/types";
import { trackEvent } from "@/lib/audit";
import { goalProbability, defenderPressure, keyPassProbability } from "@/lib/tacticalAnalytics";
import { pickAnalogForState, type AnalogResult } from "./historicalAnalogEngine";
import { t, getStoredLang } from "@/lib/i18n";

export type OracleIntent =
  | "next_goal"
  | "final_result"
  | "mvp"
  | "top_scorer"
  | "top_assister"
  | "underperformer"
  | "comeback_probability"
  | "historical_comparison"
  | "tactical_explanation"
  | "decisive_moment";

export interface OracleContext {
  minute: number;
  scoreMex: number;
  scoreArg: number;
  ballX: number;
  ballY: number;
  players: Player[];
  momentum: number;
  pressureHigh?: boolean;
  redCard?: boolean;
}

export interface OracleAnswer {
  intent: OracleIntent;
  confidence: number; // 0..1
  headline: string;
  detail: string;
  analog?: AnalogResult;
  needsLlmNarrative: boolean;
}

function topByRole(players: Player[], role: Player["role"]): Player | undefined {
  return players.find((p) => p.role === role);
}

function pickPlayerNear(players: Player[], x: number, y: number): Player | undefined {
  return [...players].sort(
    (a, b) => Math.hypot(a.x - x, a.y - y) - Math.hypot(b.x - x, b.y - y),
  )[0];
}

export function answerNextGoal(ctx: OracleContext): OracleAnswer {
  const def = defenderPressure(ctx.ballX, ctx.ballY, ctx.players);
  const xg = goalProbability(ctx.ballX, ctx.ballY, def);
  const lang = getStoredLang();
  const team = t(ctx.ballX > 0 ? "team_arg" : "team_mex", lang);
  return {
    intent: "next_goal",
    confidence: Math.min(0.95, 0.5 + xg / 2),
    headline: t("oracle_next_goal_headline", lang, { team, pct: (xg * 100).toFixed(0) }),
    detail: t("oracle_next_goal_detail", lang, {
      xg: (xg * 100).toFixed(0),
      def,
      mom: (ctx.momentum * 100).toFixed(0),
    }),
    needsLlmNarrative: false,
  };
}

export function answerFinalResult(ctx: OracleContext): OracleAnswer {
  const diff = ctx.scoreMex - ctx.scoreArg;
  const remaining = Math.max(0, 90 - ctx.minute);
  let pMex = 0.34, pDraw = 0.32, pArg = 0.34;
  if (diff > 0) { pMex += 0.2; pArg -= 0.15; pDraw -= 0.05; }
  if (diff < 0) { pArg += 0.2; pMex -= 0.15; pDraw -= 0.05; }
  if (remaining < 10) { pDraw += diff === 0 ? 0.2 : 0; }
  const total = pMex + pDraw + pArg;
  pMex /= total; pDraw /= total; pArg /= total;
  const lang = getStoredLang();
  return {
    intent: "final_result",
    confidence: 0.7,
    headline: t("oracle_final_headline", lang, {
      mex: (pMex * 100).toFixed(0),
      draw: (pDraw * 100).toFixed(0),
      arg: (pArg * 100).toFixed(0),
    }),
    detail: t("oracle_final_detail", lang, { rem: remaining, diff }),
    analog: pickAnalogForState({
      scoreDiff: diff,
      minute: ctx.minute,
      pressureHigh: !!ctx.pressureHigh,
      redCard: !!ctx.redCard,
    }),
    needsLlmNarrative: true,
  };
}

export function answerMvp(ctx: OracleContext): OracleAnswer {
  const lang = getStoredLang();
  const fwd = topByRole(ctx.players, "FWD");
  const name = fwd?.name ?? t("oracle_mvp_default", lang);
  return {
    intent: "mvp",
    confidence: 0.6,
    headline: t("oracle_mvp_headline", lang, { player: name }),
    detail: t("oracle_mvp_detail", lang),
    needsLlmNarrative: true,
  };
}

export function answerTopScorer(ctx: OracleContext): OracleAnswer {
  const lang = getStoredLang();
  const candidate = pickPlayerNear(ctx.players.filter((p) => p.role === "FWD" || p.role === "MID"), 30, 0);
  const name = candidate?.name ?? t("oracle_topscorer_default", lang);
  return {
    intent: "top_scorer",
    confidence: 0.55,
    headline: t("oracle_topscorer_headline", lang, { player: name }),
    detail: t("oracle_topscorer_detail", lang),
    needsLlmNarrative: false,
  };
}

export function answerTopAssister(ctx: OracleContext): OracleAnswer {
  const lang = getStoredLang();
  const mid = topByRole(ctx.players, "MID");
  const kp = keyPassProbability(ctx.ballX, ctx.momentum);
  return {
    intent: "top_assister",
    confidence: 0.55,
    headline: t("oracle_assist_headline", lang, {
      player: mid?.name ?? t("oracle_assist_default", lang),
    }),
    detail: t("oracle_assist_detail", lang, { pct: (kp * 100).toFixed(0) }),
    needsLlmNarrative: false,
  };
}

export function answerUnderperformer(ctx: OracleContext): OracleAnswer {
  const lang = getStoredLang();
  const tired = [...ctx.players].sort((a, b) => (b.fatigue ?? 0) - (a.fatigue ?? 0))[0];
  const name = tired?.name ?? t("oracle_under_default", lang);
  return {
    intent: "underperformer",
    confidence: 0.5,
    headline: t("oracle_under_headline", lang, { player: name }),
    detail: t("oracle_under_detail", lang, { pct: ((tired?.fatigue ?? 0) * 100) | 0 }),
    needsLlmNarrative: false,
  };
}

export function answerComeback(ctx: OracleContext): OracleAnswer {
  const lang = getStoredLang();
  const diff = ctx.scoreMex - ctx.scoreArg;
  const analog = pickAnalogForState({
    scoreDiff: diff,
    minute: ctx.minute,
    pressureHigh: !!ctx.pressureHigh,
    redCard: !!ctx.redCard,
  });
  return {
    intent: "comeback_probability",
    confidence: 0.65,
    headline: t("oracle_comeback_headline", lang, { pct: (analog.successRate * 100).toFixed(0) }),
    detail: analog.narrative,
    analog,
    needsLlmNarrative: false,
  };
}

export function answerForIntent(intent: OracleIntent, ctx: OracleContext): OracleAnswer {
  void trackEvent("ai_oracle_query", { intent });
  let res: OracleAnswer;
  switch (intent) {
    case "next_goal": res = answerNextGoal(ctx); break;
    case "final_result": res = answerFinalResult(ctx); break;
    case "mvp": res = answerMvp(ctx); break;
    case "top_scorer": res = answerTopScorer(ctx); break;
    case "top_assister": res = answerTopAssister(ctx); break;
    case "underperformer": res = answerUnderperformer(ctx); break;
    case "comeback_probability": res = answerComeback(ctx); break;
    default:
      {
        const lang = getStoredLang();
      res = {
        intent,
        confidence: 0.4,
        headline: t("oracle_default_headline", lang),
        detail: t("oracle_default_detail", lang),
        needsLlmNarrative: true,
      };
      }
  }
  void trackEvent("ai_oracle_response", {
    intent,
    confidence: res.confidence,
    needs_llm: res.needsLlmNarrative,
  });
  return res;
}

/** Map a free-text question to a deterministic oracle intent (or undefined). */
export function detectOracleIntent(question: string): OracleIntent | undefined {
  const q = question.toLowerCase();
  if (/(pr[oó]ximo|next).*(gol|goal)/.test(q)) return "next_goal";
  if (/(remontada|comeback)/.test(q)) return "comeback_probability";
  if (/(resultado|final|gana|wins|winner)/.test(q)) return "final_result";
  if (/mvp|figura/.test(q)) return "mvp";
  if (/(quien anota|top scorer|goleador|m[aá]s probable.*gol)/.test(q)) return "top_scorer";
  if (/(asistencia|assist|asistente)/.test(q)) return "top_assister";
  if (/(bajo (rendimiento|nivel)|underperform|fatiga)/.test(q)) return "underperformer";
  if (/(hist[oó]ric|hist[oó]rico|hist[oó]rica|comparable)/.test(q)) return "historical_comparison";
  if (/(t[aá]ctic|presi[oó]n|formaci[oó]n)/.test(q)) return "tactical_explanation";
  return undefined;
}
